/**
 * Wall hanging ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_paint_crafts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const wallHangingIdeasPack: ToilePack = {
    slug: 'wall-hanging-ideas',
    category: 'craft',
    name: 'Wall hanging ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'macrame', label: 'Macrame', subject: 'a macrame wall hanging on a wooden dowel'},
        {key: 'paper', label: 'Paper', subject: 'a paper flower wall hanging'},
        {key: 'beads', label: 'Beads', subject: 'a beaded wall hanging'},
    ],
    styles: [
        {key: 'boho', label: 'Boho', style: 'natural fibers with dried pampas grass tucked in', palette: 'cream cotton, sand and terracotta', framing: 'on a white wall above a rattan bench and a potted plant, the whole hanging in view'},
        {key: 'pastel', label: 'Pastel', style: 'light and airy with soft rounded shapes', palette: 'pastel pink, peach and mint', framing: 'on a pale wall above a neatly made bed, front view'},
        {key: 'festive', label: 'Colorful', style: 'bold and joyful with dense layers', palette: 'bright magenta, marigold and turquoise', framing: 'beside a doorway on a white wall, front view, the hanging centered'},
    ],
    directions: ['for Diwali', 'with shells', 'nursery'],
    directionHint: 'try: for Diwali',
    seo: {
        title: 'Wall hanging ideas: macrame, paper, beads',
        metaDescription: 'Nine wall hanging ideas in macrame, paper flowers and beads, in boho, pastel and colourful styles, shown on real walls.',
        h1: 'Wall hanging ideas: macrame, paper flower, beaded',
        tagline: 'Three materials, each in a boho, a pastel and a colourful look on a home wall.',
        intro: 'A wall hanging is a simple way to dress a bare wall with craft supplies. These nine show three materials: macrame on a wooden dowel, a paper flower hanging and a beaded hanging. Each is made in a boho, pastel or colourful style, and shown on a wall above a bench, above a bed or beside a doorway. Type a theme, such as for Diwali, to ask for it.',
        useCases: [
            'a wall decoration for a living room',
            'a nursery wall hanging',
            'a Diwali decoration',
            'a gift for a new home',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Macrame, paper flower and beaded wall hangings, each in a boho, pastel and colourful style.'},
            {q: 'Are these photos of real hangings?', a: 'No. They are generated pictures of each design, not photographs of hangings made by someone.'},
            {q: 'What materials are shown?', a: 'Knotted macrame cord on a wooden dowel, paper flowers and beads. The boho style uses natural fibres with dried pampas grass tucked in.'},
            {q: 'Can I ask for a different theme?', a: 'Yes. Type a theme such as with shells or nursery. A typed theme is a request, not a promised result.'},
        ],
    },
}
